import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import {
  createContext,
  FC,
  useCallback,
  useMemo,
  useState,
  useEffect
} from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import { parseParametrizatioRevisionData } from '@helper/grid'
import parseFilters from '@helper/parseFilters'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrization from '@service/parametrization'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import type {
  IParametrizationRevisionContext,
  IParams,
  Pagination,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsEdit,
  IUpdateParamsDate
} from '@base/@types/providers/Parametrization/RevisionProvider'
import parametrizationRevisionHeader from '@base/variables/headers/parametrization/revision'

const ParametrizationRevisionProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(false)
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('PRM Revisão - Relatório')
  const [fetching, _setFetching] = useState(false)
  const [typeGrid, setTypeGrid] = useState(1)
  const [_error, setError] = useState<string | undefined>()

  const [data, updateData] = useImmer<any[]>([])
  const [meta, _updateMeta] = useImmer({})

  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])

  const [selecteds, setSelecteds] = useImmer([] as any)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [filterEdit, updateCenters] = useImmer({
    centers: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const handleToogleTable = ({ name, value }: ITooglesParams) => {
    updateToogleParams(draft => {
      draft[name] = value
    })
  }

  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() - 30)

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(dateEnd).format('YYYY-MM-DD'),
    dateEnd: dayjs(new Date()).format('YYYY-MM-DD')
  })

  const handleParamsDate = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParams(draft => {
      draft[name] = value
    })
  }

  const [paramsEdit, updateEditParams] = useImmer({
    abastercer: 0,
    tipoSuprimento: 0,
    politicaEstoque: 0,
    incluirPrevisaoLeadTime: 0,
    maximo: 0,
    frequenciaDePedidos: 0,
    leadTime: 0,
    tempoDeEspera: 0,
    loteMultiplo: 0,
    politicaDeEstoqueArbitraria: 0,
    loteMinimoCD: 0,
    estoqueArbitrado: 0,
    cdDeOrigem: 0,
    motivo: 'Teste'
  })

  const handleUpdateEditParams = ({ name, value }: IUpdateParamsEdit) => {
    updateEditParams(draft => {
      if (name === 'motivo') {
        draft[name] = value
      } else {
        draft[name] = value
      }
    })
  }

  const getCenters = useCallback(async () => {
    try {
      const { data: centers } = await parametrization.getCenters()

      updateCenters(draft => {
        return Object.assign(draft, {
          centers: parseFilters.parse(centers)
        })
      })
    } catch (error: any) {
      const { data } = error.response

      setError(
        data.message ||
          'Ocorreu um problema interno. Tente novamente mais tarde'
      )
    }
    setLoading(false)
  }, [updateCenters])

  const handleSelectAll = () => {
    if (selecteds.length < data.length) {
      const res = []
      for (const item of data) {
        res.push({
          produtoId: item.idProduto,
          idLoja: item.loja
        })
      }
      setSelecteds(res)
    } else {
      setSelecteds([])
    }
  }

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    statusIds: [],
    curvaAbc: [],
    curvaIgualZero: false,
    produtoSemFornecedor: false
  })

  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationRevisionHeader
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationRevisionHeader')) {
      encrypteHeaderStorange('parametrizationRevisionHeader', headers)
    } else {
      localStorage.removeItem('parametrizationRevisionHeader')
      encrypteHeaderStorange('parametrizationRevisionHeader', headers)
    }
  }, [headers])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]
      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationRevisionHeader')) {
      const newHeader = decrypterHeaderStorange('parametrizationRevisionHeader')

      newHeader[0].columns[0] = {
        id: newHeader[0].columns[0].id,
        name: newHeader[0].columns[0].name,
        show: newHeader[0].columns[0].show
      }
      updateHeaders(newHeader)
    }
  }, [updateHeaders])
  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams(draft => {
      if (name === 'curvaIgualZero') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
      if (name === 'produtoSemFornecedor') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
    })
  }

  const handledTypeGrid = (value: number) => {
    setTypeGrid(value)
  }
  const exportData = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const paramsToSend = {
      dataInicio: paramsDates.dateInit.toLocaleString(),
      dataFim: paramsDates.dateEnd.toLocaleString()
    }
    try {
      const { data: res } = await parametrization.postExportRevision(
        paramsToSend
      )
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      Swal.close()
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
  }, [paramsDates.dateInit, paramsDates.dateEnd, filename, toast])

  const handlePageChange = useCallback(
    async (page: number) => {
      setLoading(true)
      parametrization
        .getMinimumBilling({
          page
        })
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })

          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => updateData([]))
      setLoading(false)
    },
    [updateData, updatePagination]
  )
  const getData = useCallback(async () => {
    setLoading(true)
    // await getMaintence()
    encrypteHeaderStorange('parametrizationRevisionHeader', headers)

    handleStorange()
    const paramsToSend = {
      dataInicio: paramsDates.dateInit.toLocaleString(),
      dataFim: paramsDates.dateEnd.toLocaleString()
    }

    try {
      const { data: res } = await parametrization.getRevisionData({
        params: paramsToSend
      })

      const { data } = res

      updateData(draft => {
        return draft.filter(item => item.status === 99).concat(data)
      })
      setLoading(false)
    } catch {
      setLoading(false)
    }
  }, [
    handleStorange,
    headers,
    paramsDates.dateEnd,
    paramsDates.dateInit,
    updateData
  ])

  const approved = useCallback(async () => {
    const approved = []
    for (const item of selecteds) {
      approved.push({
        id: item,
        empresaId: data.filter(x => x.id === item)[0].loja.empresaId
      })
    }

    try {
      const { data: _res } = await parametrization.putAprovedRevision({
        params: {
          requisicao: approved
        }
      })
      toast({
        title: 'Sucesso.',
        description: 'Aprovado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setSelecteds([])
    getData()
    setLoading(false)
  }, [selecteds, data, getData, setLoading, toast, setSelecteds])

  const recused = useCallback(async () => {
    const approved = []
    for (const item of selecteds) {
      approved.push({
        id: item,
        empresaId: data.filter(x => x.id === item)[0].loja.id
      })
    }

    try {
      const { data: _res } = await parametrization.putRecusedRevision({
        params: {
          requisicao: approved,
          motivo: paramsEdit.motivo
        }
      })
      toast({
        title: 'Sucesso.',
        description: 'Recusado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setSelecteds([])
    getData()
    setLoading(false)
  }, [selecteds, data, setSelecteds, getData, setLoading, paramsEdit, toast])

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return []
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'parameterRevision',
            data,
            headers,
            parser: parseParametrizatioRevisionData
          }
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [toggleColumn, data, headers])

  const handleSelect = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']

      if (!selecteds.includes(similarProduct)) {
        setSelecteds([...selecteds, similarProduct])
      } else {
        setSelecteds(selecteds.filter((prop: any) => prop !== similarProduct))
      }
    },
    [setSelecteds, selecteds]
  )

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        onChange: handleSelect,
        selecteds: selecteds,
        isMulti: true
      }
    ]
  }, [selecteds, handleSelect])

  const tableInteractionApproved = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        icon: <AiOutlineBarChart />
      }
    ]
  }, [])

  const tableInteractionRecused = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        icon: <AiOutlineBarChart />
      }
    ]
  }, [])

  useEffect(() => {
    setSelecteds(selecteds)
  }, [selecteds, setSelecteds])

  return (
    <ParametrizationRevisionContext.Provider
      value={{
        loading,
        data: parseParametrizatioRevisionData(data),
        meta,
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        tableInteractionApproved,
        tableInteractionRecused,
        selecteds,
        filterEdit,
        paramsEdit,
        paramsDates,
        typeGrid,
        pagination,
        tooglesTable,
        dataPendentes: parseParametrizatioRevisionData(
          data.filter((data: any) => data.status === 1)
        ),
        dataAprovados: parseParametrizatioRevisionData(
          data.filter((data: any) => data.status === 2)
        ),
        dataRecusados: parseParametrizatioRevisionData(
          data.filter((data: any) => data.status === 3)
        ),

        filename,
        query,
        filters,
        hiddenColumns,
        handlePageChange,

        filtersList,
        toggleColumn,
        handleUpdateParams,
        handleSelectAll,
        handleSelect,
        handleParamsDate,
        setQuery,
        getData,
        exportData,
        handleUpdateEditParams,
        handleToogleTable,
        approved,
        getCenters,
        recused,
        setFilters,
        handledTypeGrid,
        setFilename
      }}
    >
      {children}
    </ParametrizationRevisionContext.Provider>
  )
}

export default ParametrizationRevisionProvider
export const ParametrizationRevisionContext =
  createContext<IParametrizationRevisionContext>(
    {} as IParametrizationRevisionContext
  )
